import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX, IconLoader2 } from '@tabler/icons-react';
import './checkInDialog.css';

export interface CancelSessionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}

/**
 * Destructive confirmation modal for cancelling a check-in session (sm4rt-web-ui
 * skill §6 — full-attention destructive confirmation). On-system shell (shared
 * checkInDialog.css): portal + backdrop, hairline header with close ×, reason
 * textarea, recessive "Keep session" + solid-red confirm. Shared by the session
 * detail page and the dashboard check-in row's cancel action.
 */
export const CancelSessionDialog: React.FC<CancelSessionDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleClose = () => {
    if (submitting) return;
    setReason('');
    setError(null);
    onClose();
  };

  // Focus the reason field on open; Escape closes (when not submitting).
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => textareaRef.current?.focus(), 0);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitting]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Cancellation reason is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      setReason('');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !submitting) handleClose();
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={handleBackdropMouseDown}>
      <div className="ci-dialog" role="dialog" aria-modal="true" aria-label="Cancel Check-In Session">
        <div className="ci-dialog__header">
          <span className="ci-dialog__title">Cancel Check-In Session</span>
          <button
            type="button"
            className="ci-dialog__close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Close"
          >
            <IconX size={14} stroke={1.5} />
          </button>
        </div>

        <div className="ci-dialog__body">
          <div className="ci-dialog__desc">
            The session will be soft-deleted and its token disabled. Reception can recover
            it from the dashboard if needed.
          </div>

          <label className="ci-dialog__field-label" htmlFor="cancel-session-reason">
            Reason for cancellation
          </label>
          <textarea
            id="cancel-session-reason"
            ref={textareaRef}
            className={`ci-dialog__textarea${error ? ' ci-dialog__textarea--error' : ''}`}
            placeholder="Why is this session being cancelled?"
            value={reason}
            onChange={e => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
            disabled={submitting}
          />

          {error && <div className="ci-dialog__error">{error}</div>}
        </div>

        <div className="ci-dialog__footer">
          <button
            type="button"
            className="ci-dialog__cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            Keep session
          </button>
          <button
            type="button"
            className="ci-dialog__confirm ci-dialog__confirm--danger"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting && <IconLoader2 size={14} stroke={1.5} className="ci-dialog__spin" />}
            {submitting ? 'Cancelling…' : 'Cancel Session'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
